import React, { useEffect, useRef } from 'react';

/**
 * Spotlight que sigue al cursor y deja ver una segunda capa (`reveal`) a
 * través de una máscara circular de borde suave.
 *
 * Uso:
 *   <Spotlight reveal={<CapaOculta />} revealClassName="z-10" className="relative h-svh">
 *     <video className="absolute inset-0 z-0" ... />
 *     <div className="relative z-20">Contenido encima de ambas capas</div>
 *   </Spotlight>
 *
 * La posición y el radio se interpolan en un rAF (nada de setState por
 * movimiento del mouse) y se escriben como variables CSS que consume la
 * máscara de `.spotlight-layer` en index.css. En pantallas táctiles no hay
 * cursor: el foco deriva solo, despacio, mientras la sección está visible.
 */
export default function Spotlight({ children, reveal, radius = 260, className = '', revealClassName = '' }) {
    const rootRef = useRef(null);
    const layerRef = useRef(null);

    useEffect(() => {
        const root = rootRef.current;
        const layer = layerRef.current;
        if (!root || !layer) return undefined;

        const finePointer = window.matchMedia('(pointer: fine)').matches;
        const pos = { x: root.clientWidth / 2, y: root.clientHeight / 2 };
        const target = { ...pos };
        let r = 0;
        let targetR = 0;
        let rafId = null;
        let visible = true;

        const write = () => {
            layer.style.setProperty('--sx', `${pos.x}px`);
            layer.style.setProperty('--sy', `${pos.y}px`);
            layer.style.setProperty('--sr', `${Math.max(r, 0)}px`);
        };

        const drift = (t) => {
            // Trayectoria de Lissajous: lenta y sin repetirse a simple vista
            const w = root.clientWidth;
            const h = root.clientHeight;
            target.x = w * (0.5 + 0.28 * Math.sin(t / 2900));
            target.y = h * (0.45 + 0.2 * Math.sin(t / 2300 + 1.3));
        };

        const tick = (t) => {
            if (!finePointer) drift(t);
            pos.x += (target.x - pos.x) * 0.16;
            pos.y += (target.y - pos.y) * 0.16;
            r += (targetR - r) * 0.1;
            write();

            const settled = Math.abs(target.x - pos.x) < 0.3 && Math.abs(target.y - pos.y) < 0.3 && Math.abs(targetR - r) < 0.3;
            // Con mouse: corre hasta alcanzar el cursor. En táctil: mientras se vea la sección.
            const keepGoing = finePointer ? !settled : visible;
            rafId = keepGoing ? requestAnimationFrame(tick) : null;
        };

        const start = () => {
            if (!rafId) rafId = requestAnimationFrame(tick);
        };

        const onMove = (e) => {
            const rect = root.getBoundingClientRect();
            target.x = e.clientX - rect.left;
            target.y = e.clientY - rect.top;
            targetR = radius;
            start();
        };

        const onLeave = () => {
            targetR = 0;
            start();
        };

        write();

        if (finePointer) {
            root.addEventListener('pointermove', onMove);
            root.addEventListener('pointerleave', onLeave);
            return () => {
                root.removeEventListener('pointermove', onMove);
                root.removeEventListener('pointerleave', onLeave);
                if (rafId) cancelAnimationFrame(rafId);
            };
        }

        targetR = radius * 0.75;
        const io = new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting;
            if (visible) start();
        });
        io.observe(root);
        return () => {
            io.disconnect();
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [radius]);

    return (
        <div ref={rootRef} className={className}>
            {children}
            <div ref={layerRef} aria-hidden="true" className={`spotlight-layer pointer-events-none absolute inset-0 ${revealClassName}`}>
                {reveal}
            </div>
        </div>
    );
}
